"use client";

import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { cn } from "@/lib/utils";
import styles from "./arena.module.css";

/** True when the visitor has asked for less motion; read on the client only. */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * True once the element has been on screen (`once`), which is when a
 * sequence that should be watched (the standings, the statement swap) starts.
 * Without an `IntersectionObserver` it answers true, so nothing waits forever.
 */
export function useSeenOnce<T extends Element>(ref: RefObject<T | null>, threshold = 0.25): boolean {
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || seen) return;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, seen, threshold]);

  return seen;
}

type RevealState = "idle" | "armed" | "in";

/**
 * A section that fades up once as it enters the screen (`docs/03` §7,
 * showcase motion: 240 ms, 12 px).
 *
 * Rendered `idle`, which is fully visible, so the server's HTML, a visitor
 * without JavaScript and a visitor who asked for less motion all see the page
 * as it is. Only a section still below the fold at mount is hidden and then
 * revealed; anything already on screen is never hidden, so nothing a visitor
 * is reading blinks out.
 */
export function Reveal({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<RevealState>("idle");

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof IntersectionObserver === "undefined" || prefersReducedMotion()) return;
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) return;

    setState("armed");
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setState("in");
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} data-reveal={state} className={cn(styles.reveal, className)}>
      {children}
    </div>
  );
}
